// The venue logo and the venue's own message texts on the settings page (issue #20, US-5). The logo uploads on its
// own as soon as a file is picked (PNG or SVG, checked with the same core rule as the API); the texts are part of the
// settings form and its one save. Each text has a 160-character counter, the variables it must keep, a live preview
// of the WhatsApp message and of the backup SMS, and a restore-the-default button. Every state carries a symbol or
// words, never color alone; texts are edited and previewed in their own language and direction. Darija is not in
// release 1: only Arabic, French and English take a text.
import { useRef, useState, type ChangeEvent } from 'react';
import {
  CLOSING_LINE,
  LANG_DIR,
  LANG_LABELS,
  LOGO_ACCEPT,
  MESSAGE_MAX_CHARS,
  MESSAGE_PLACEHOLDERS,
  OVERRIDE_KINDS,
  REQUIRED_PLACEHOLDERS,
  SUPPORTED_LANGS,
  UI,
  checkLogo,
  checkMessageOverride,
  defaultMessageText,
  fill,
  isDefaultMessageText,
  isLogoError,
  logoErrorCopy,
  logoMaxKb,
  messageErrorCopy,
  messageLabel,
  normalizeMessageText,
  placeholderToken,
  previewMessage,
  type Lang,
  type MessageContext,
  type MessageOverrideError,
  type MessageOverrides,
  type MessagePlaceholder,
  type MessagesPatch,
  type OverrideKind,
  type UiCopy,
} from '@ringring/core';
import type { ApiError } from './api';

// ---- the logo ----

export interface LogoCardProps {
  lang: Lang;
  venueName: string;
  logoUrl: string | null;
  /** Sends the file; throws the ApiError on a refusal so the card shows its copy. */
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}

type LogoStatus = 'idle' | 'busy' | 'saved' | 'removed';

/** The logo section: the current logo (or the line saying there is none), pick a file, remove, and the outcome. */
export function LogoCard({ lang, venueName, logoUrl, onUpload, onRemove }: LogoCardProps) {
  const t = UI[lang];
  const input = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<LogoStatus>('idle');
  const [problem, setProblem] = useState<string | null>(null);

  const fail = (err: unknown) => {
    const e = err as ApiError;
    setStatus('idle');
    setProblem(isLogoError(e.code) ? logoErrorCopy(lang, e.code) : t.logo_not_saved);
  };

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // the same file can be picked again after a refusal
    if (!file) return;
    setProblem(null);
    const checked = checkLogo(new Uint8Array(await file.arrayBuffer()));
    if (checked.error) {
      setStatus('idle');
      setProblem(logoErrorCopy(lang, checked.error));
      return;
    }
    setStatus('busy');
    try {
      await onUpload(file);
      setStatus('saved');
    } catch (err) {
      fail(err);
    }
  };

  const remove = async () => {
    setProblem(null);
    setStatus('busy');
    try {
      await onRemove();
      setStatus('removed');
    } catch (err) {
      fail(err);
    }
  };

  return (
    <section className="card" aria-labelledby="s-logo">
      <h2 id="s-logo">{t.logo_section}</h2>
      <p className="muted">{t.logo_intro}</p>
      {logoUrl ? (
        <figure className="logo-preview">
          <img src={logoUrl} alt={venueName} />
          <figcaption className="muted">{t.logo_current}</figcaption>
        </figure>
      ) : (
        <p className="muted">
          <span aria-hidden="true">—</span> {t.logo_none}
        </p>
      )}
      <div className="row">
        <button type="button" className="btn secondary" onClick={() => input.current?.click()} disabled={status === 'busy'} aria-describedby="s-logo-hint">
          {logoUrl ? t.logo_replace : t.logo_choose}
        </button>
        <input ref={input} id="s-logo-file" type="file" accept={LOGO_ACCEPT} onChange={(e) => void pick(e)} hidden />
        {logoUrl ? (
          <button type="button" className="btn secondary" onClick={() => void remove()} disabled={status === 'busy'}>
            {t.logo_remove}
          </button>
        ) : null}
        {status === 'busy' ? (
          <span className="tag" role="status">
            <span aria-hidden="true">…</span> {t.logo_uploading}
          </span>
        ) : status === 'saved' || status === 'removed' ? (
          <span className="tag ready" role="status">
            <span aria-hidden="true">✓</span> {status === 'saved' ? t.logo_saved : t.logo_removed}
          </span>
        ) : null}
      </div>
      <p id="s-logo-hint" className="muted">
        {fill(t.logo_hint, { max: String(logoMaxKb()) })}
      </p>
      {problem ? (
        <p className="error" role="alert">
          <span aria-hidden="true">✕</span> {problem}
        </p>
      ) : null}
    </section>
  );
}

// ---- the message texts ----

/** The text in each editor, per language and message: the venue's own text or the default. */
export type MessageDrafts = Record<Lang, Record<OverrideKind, string>>;

/** The editors as the page opens: every language, both messages, the stored text or the default. */
export function initialDrafts(overrides: MessageOverrides | null | undefined): MessageDrafts {
  const out = {} as MessageDrafts;
  for (const l of SUPPORTED_LANGS) {
    out[l] = {} as Record<OverrideKind, string>;
    for (const k of OVERRIDE_KINDS) out[l][k] = overrides?.[l]?.[k] ?? defaultMessageText(k, l);
  }
  return out;
}

/** Only the texts that changed since `baseline`, as the settings body carries them: a text, or null back to the default. */
export function changedMessages(baseline: MessageDrafts, drafts: MessageDrafts): MessagesPatch {
  const out: MessagesPatch = {};
  for (const l of SUPPORTED_LANGS) {
    for (const k of OVERRIDE_KINDS) {
      if (normalizeMessageText(drafts[l][k]) === normalizeMessageText(baseline[l][k])) continue;
      (out[l] ??= {})[k] = isDefaultMessageText(k, l, drafts[l][k]) ? null : drafts[l][k];
    }
  }
  return out;
}

/** The first changed text that would be refused, in the picker order, with its language and message. */
export function firstMessageProblem(baseline: MessageDrafts, drafts: MessageDrafts, sample: MessageContext): { lang: Lang; kind: OverrideKind; error: MessageOverrideError; placeholder?: string } | null {
  const changed = changedMessages(baseline, drafts);
  for (const l of PICKER_ORDER) {
    for (const k of OVERRIDE_KINDS) {
      const text = changed[l]?.[k];
      if (typeof text !== 'string') continue;
      const checked = checkMessageOverride(k, l, text, sample);
      if (checked.error !== undefined) return { lang: l, kind: k, error: checked.error, placeholder: checked.placeholder };
    }
  }
  return null;
}

/** The picker order from the copy library: العربية · Français · English. */
const PICKER_ORDER: readonly Lang[] = ['ar', 'fr', 'en'];

const PLACEHOLDER_LABEL: Record<MessagePlaceholder, keyof UiCopy> = {
  order_number: 'placeholder_order_number',
  venue_name: 'placeholder_venue_name',
  pickup_point: 'placeholder_pickup_point',
};

export interface MessagesEditorProps {
  lang: Lang;
  /** The languages the venue offers (the form's current choice); only these get an editor. */
  languages: readonly Lang[];
  active: Lang;
  onActive: (l: Lang) => void;
  drafts: MessageDrafts;
  onChange: (l: Lang, k: OverrideKind, text: string) => void;
  /** What the counter and the preview fill the variables with: the venue name, its longest pickup point, the widest number. */
  sample: MessageContext;
}

/** The messages section: a language picker, then the registration and the ready message of that language. */
export function MessagesEditor({ lang, languages, active, onActive, drafts, onChange, sample }: MessagesEditorProps) {
  const t = UI[lang];
  const offered = PICKER_ORDER.filter((l) => languages.includes(l));
  const shown = offered.includes(active) ? active : offered[0];
  return (
    <section className="card" aria-labelledby="s-messages">
      <h2 id="s-messages">{t.messages_section}</h2>
      <p className="muted">{t.messages_intro}</p>
      <p className="muted">{fill(t.messages_count_hint, { max: String(MESSAGE_MAX_CHARS) })}</p>
      {offered.length > 1 ? (
        <div className="filter" role="group" aria-label={t.message_lang}>
          {offered.map((l) => (
            <button type="button" className="chip" key={l} lang={l} aria-pressed={l === shown} onClick={() => onActive(l)}>
              {l === shown ? <span aria-hidden="true">✓</span> : null}
              {LANG_LABELS[l]}
            </button>
          ))}
        </div>
      ) : null}
      {shown
        ? OVERRIDE_KINDS.map((k) => <MessageField key={`${shown}-${k}`} uiLang={lang} lang={shown} kind={k} value={drafts[shown][k]} onChange={(text) => onChange(shown, k, text)} sample={sample} />)
        : null}
    </section>
  );
}

interface MessageFieldProps {
  uiLang: Lang;
  lang: Lang;
  kind: OverrideKind;
  value: string;
  onChange: (text: string) => void;
  sample: MessageContext;
}

/** One text: label and state, the editor in its language, the variables to insert, the counter, the preview, restore. */
function MessageField({ uiLang, lang, kind, value, onChange, sample }: MessageFieldProps) {
  const t = UI[uiLang];
  const area = useRef<HTMLTextAreaElement>(null);
  const id = `msg-${lang}-${kind}`;
  const preview = previewMessage(kind, lang, value, sample);
  const checked = checkMessageOverride(kind, lang, value, sample);
  const error = checked.error !== undefined ? messageErrorCopy(uiLang, checked.error, checked.placeholder) : null;
  const over = preview.length - MESSAGE_MAX_CHARS;

  /** Puts a variable where the cursor is (at the end when the editor never had focus) and keeps typing after it. */
  const insert = (p: MessagePlaceholder) => {
    const token = placeholderToken(p);
    const el = area.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    if (el) {
      window.requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    }
  };

  return (
    <div className="message-field">
      <div className="row">
        <label htmlFor={id}>{messageLabel(uiLang, kind)}</label>
        <span className="tag">{preview.isDefault ? `○ ${t.message_default_tag}` : `✎ ${t.message_custom_tag}`}</span>
      </div>
      <p className="muted" id={`${id}-hint`}>
        {kind === 'ready' ? t.message_ready_hint : t.message_registered_hint}
      </p>
      <textarea
        id={id}
        ref={area}
        lang={lang}
        dir={LANG_DIR[lang]}
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={`${id}-hint ${id}-count${error ? ` ${id}-error` : ''}`}
        aria-invalid={error ? true : undefined}
      />
      <div className="row" role="group" aria-label={t.message_insert}>
        {MESSAGE_PLACEHOLDERS.map((p) => (
          <button type="button" className="chip" key={p} onClick={() => insert(p)}>
            <span aria-hidden="true">+</span> {t[PLACEHOLDER_LABEL[p]]}
          </button>
        ))}
      </div>
      <p className="muted">
        {t.message_required}{' '}
        {REQUIRED_PLACEHOLDERS[kind].map((p, i) => (
          <span key={p}>
            {i > 0 ? ' ' : ''}
            <code>
              <bdi dir="ltr">{placeholderToken(p)}</bdi>
            </code>
          </span>
        ))}
      </p>
      {kind === 'ready' ? (
        <p className="muted">
          {t.message_closing}{' '}
          <span lang={lang} dir={LANG_DIR[lang]}>
            {CLOSING_LINE[lang]}
          </span>
        </p>
      ) : null}
      <p className="muted counter" id={`${id}-count`} aria-live="polite">
        {over > 0 ? (
          <>
            <span aria-hidden="true">✕</span> {fill(t.message_over, { count: String(over) })} ·{' '}
          </>
        ) : null}
        {fill(t.message_counter, { count: String(preview.length), max: String(MESSAGE_MAX_CHARS) })}
      </p>
      {error ? (
        <p className="error" id={`${id}-error`}>
          <span aria-hidden="true">✕</span> {error}
        </p>
      ) : null}
      <div className="preview">
        <p className="preview-label">{t.message_preview}</p>
        <p className="bubble" lang={lang} dir={LANG_DIR[lang]}>
          {preview.text}
        </p>
        {preview.sms ? (
          <>
            <p className="preview-label">{t.message_sms_preview}</p>
            <p className="bubble" lang={lang} dir={LANG_DIR[lang]}>
              {preview.sms.text}
            </p>
            {preview.sms.source === 'default' && !preview.isDefault ? (
              <p className="muted">
                <span aria-hidden="true">⚠</span> {t.message_sms_default}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="row">
        <button type="button" className="btn secondary" onClick={() => onChange(defaultMessageText(kind, lang))} disabled={preview.isDefault}>
          {t.message_restore}
        </button>
      </div>
    </div>
  );
}
